// mention-typeahead.js — caret-anchored @-mention dropdown over a <textarea> (TPT33).
// Sibling of components/member-combo.js / lib/tag-typeahead.js: same body-portaled,
// position:fixed dropdown convention (so .task-detail-body's own overflow-y:auto can
// never clip it), same mousedown+preventDefault commit discipline, same capture-phase
// scroll/resize repositioning.
//
// Ported from the Task App's _activeMention/_caretPoint/_refreshMentionDropdown/
// _selectMention (ai/todo/server/src/client/task-board.js:4503-4700) with two
// deliberate changes:
//   1. Our dropdown is position:fixed (the Task App's is position:absolute on
//      document.body), so the mirror-div caret measurement must NOT add
//      window.scrollX/scrollY — those offsets would send the dropdown off-screen on
//      any scrolled page.
//   2. Token detection reuses MENTION_BOUNDARY_BEFORE from mention-highlight.js — the
//      SAME leading-boundary class api/src/lib/mentions.js's parseMentions() uses — so
//      "does the dropdown open here" and "will the server resolve this token" never
//      disagree. (The Task App's own _activeMention uses a plain `\s` boundary.)

import { escapeHtml, renderAvatarHtml } from '../utils.js';
import { MENTION_BOUNDARY_BEFORE } from './mention-highlight.js';

const TOKEN_RE = new RegExp(`(?:^|${MENTION_BOUNDARY_BEFORE})@([^@\\n]{0,40})$`);

// activeMentionToken(value, caret) -> {start, end, query} | null. `caret` must be a
// collapsed-selection position — callers pass null for a range selection, so no
// dropdown opens mid-drag-select. Pure, no DOM.
export function activeMentionToken(value, caret) {
  if (caret == null) return null;
  const before = String(value ?? '').slice(0, caret);
  const m = before.match(TOKEN_RE);
  if (!m) return null;
  return { start: caret - m[1].length - 1, end: caret, query: m[1] };
}

function acceptedMentionMembers(members) {
  return (members || []).filter((m) => m && m.user_id != null);
}

function memberHandle(m) {
  if (m.name && String(m.name).trim()) return String(m.name).trim();
  if (m.email) {
    const local = String(m.email).split('@')[0];
    if (local) return local;
  }
  return null;
}

// filterMentionOptions(members, query, limit) -> [{userId, handle, name, email, member}]
// Deliberately its own function, not member-combo.js's buildMemberOptions() — that one
// always prepends an "Unassigned" row, which must never appear here, and lib/
// importing from components/ would invert this app's layering. `handle` mirrors
// api/src/lib/mentions.js's own candidate rule (name, else email local-part) so every
// token this dropdown inserts is one the server will actually resolve.
export function filterMentionOptions(members, query, limit = 8) {
  const q = String(query || '').trim().toLowerCase();
  const out = [];
  for (const m of acceptedMentionMembers(members)) {
    const handle = memberHandle(m);
    if (!handle) continue;
    if (q && !(String(m.name || '').toLowerCase().includes(q) || String(m.email || '').toLowerCase().includes(q))) continue;
    out.push({ userId: m.user_id, handle, name: m.name || handle, email: m.email || '', member: m });
    if (out.length >= limit) break;
  }
  return out;
}

// Module-level registry of instances with a dropdown currently open — backs
// closeAnyOpenMentionTypeahead(), the sibling of lib/embed-menu.js's
// closeAnyOpenEmbedMenu(). closeTaskDetail() never removes panelEl from the DOM (it
// slides out via CSS class), so nothing else would force a stray body-appended
// dropdown closed when the panel closes mid-interaction.
const openInstances = new Set();
export function closeAnyOpenMentionTypeahead() {
  for (const inst of Array.from(openInstances)) inst.close();
}

const MIRROR_PROPS = [
  'boxSizing', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing', 'lineHeight', 'tabSize',
];

// attachMentionTypeahead(textarea, opts) -> { destroy, close, refresh }
// opts:
//   getMembers()   -> member[]     live list (host state can change)
//   scrollEl       -> Element|null scroll container to reposition against
//   limit                          max dropdown rows (default 8)
//   onPickStart()                  fired synchronously right before a commit
//   onPickEnd()                    fired synchronously right after a commit
export function attachMentionTypeahead(textarea, opts = {}) {
  const noop = { destroy() {}, close() {}, refresh() {} };
  if (!textarea) return noop;

  const { getMembers, scrollEl, limit = 8 } = opts;
  const onPickStart = typeof opts.onPickStart === 'function' ? opts.onPickStart : () => {};
  const onPickEnd = typeof opts.onPickEnd === 'function' ? opts.onPickEnd : () => {};

  let dropdown = null;
  let options = [];
  let activeIndex = -1;
  let token = null;
  let blurTimer = null;
  let committing = false; // guards against commit()'s own dispatched `input` re-opening

  // caretPoint() — hidden-mirror-div caret measurement, ported from the Task App's
  // _caretPoint (task-board.js:4599). Viewport-relative (NO window.scrollX/scrollY —
  // see file header) since .mention-dropdown is position:fixed, not absolute.
  function caretPoint() {
    const rect = textarea.getBoundingClientRect();
    const style = getComputedStyle(textarea);
    const mirror = document.createElement('div');
    for (const p of MIRROR_PROPS) mirror.style[p] = style[p];
    Object.assign(mirror.style, {
      position: 'fixed',
      visibility: 'hidden',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'break-word',
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      width: `${textarea.clientWidth}px`,
      minHeight: `${textarea.offsetHeight}px`,
    });
    mirror.textContent = textarea.value.slice(0, textarea.selectionStart);
    const marker = document.createElement('span');
    marker.textContent = textarea.value.slice(textarea.selectionStart) || '.';
    mirror.appendChild(marker);
    document.body.appendChild(mirror);
    const mr = marker.getBoundingClientRect();
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2 || 18;
    mirror.remove();
    return { left: mr.left - textarea.scrollLeft, top: mr.top - textarea.scrollTop, lineHeight };
  }

  function positionDropdown() {
    if (!dropdown || !token) return;
    const pt = caretPoint();
    const dw = dropdown.offsetWidth || 240;
    const dh = dropdown.offsetHeight || 0;
    const left = Math.max(8, Math.min(pt.left, window.innerWidth - dw - 8));
    let top = pt.top + pt.lineHeight + 4; // just below the caret's current line
    if (top + dh > window.innerHeight - 8) top = pt.top - dh - 4; // flip above the line
    dropdown.style.left = `${left}px`;
    dropdown.style.top = `${Math.max(8, top)}px`;
  }

  const onScrollOrResize = () => positionDropdown();
  if (scrollEl) scrollEl.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);

  function renderRow(o, i) {
    return `<div class="mention-option" data-index="${i}">${renderAvatarHtml(o.member, 20)}<span>${escapeHtml(o.name)}</span>${o.email ? `<span class="mention-option-email">${escapeHtml(o.email)}</span>` : ''}</div>`;
  }

  function updateActive() {
    if (!dropdown) return;
    dropdown.querySelectorAll('.mention-option').forEach((el, i) => el.classList.toggle('active', i === activeIndex));
  }

  function renderDropdown() {
    if (!dropdown) {
      dropdown = document.createElement('div');
      dropdown.className = 'mention-dropdown';
      dropdown.addEventListener('mousedown', onDropdownMousedown);
      document.body.appendChild(dropdown);
    }
    options = filterMentionOptions(typeof getMembers === 'function' ? getMembers() : [], token.query, limit);
    activeIndex = options.length ? 0 : -1;
    dropdown.innerHTML = options.length
      ? options.map(renderRow).join('')
      : '<div class="mention-empty">No matching members</div>';
    updateActive();
    positionDropdown();
  }

  function close() {
    if (dropdown) { dropdown.remove(); dropdown = null; }
    options = [];
    activeIndex = -1;
    token = null;
    openInstances.delete(api);
  }

  function move(delta) {
    if (!options.length) return;
    activeIndex = (activeIndex + delta + options.length) % options.length;
    updateActive();
  }

  function commit(i) {
    const o = options[i];
    if (!o || !token) return;
    onPickStart();
    const insert = `@${o.handle} `;
    textarea.value = textarea.value.slice(0, token.start) + insert + textarea.value.slice(token.end);
    const pos = token.start + insert.length;
    textarea.setSelectionRange(pos, pos);
    close();
    textarea.focus();
    // Let host listeners (auto-grow, dirty flags) see the programmatic change.
    // `committing` short-circuits our own onInput so this can't re-enter and re-open
    // the dropdown — belt-and-braces alongside the trailing space, which already
    // makes activeMentionToken() return null right after.
    committing = true;
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
    committing = false;
    onPickEnd();
  }

  function onDropdownMousedown(e) {
    const opt = e.target.closest('.mention-option');
    if (!opt) return;
    e.preventDefault(); // keep focus on the textarea — don't let the click blur it first
    commit(Number(opt.dataset.index));
  }

  function onInput() {
    if (committing) return;
    if (textarea.selectionStart !== textarea.selectionEnd) { close(); return; }
    const next = activeMentionToken(textarea.value, textarea.selectionStart);
    if (!next) { close(); return; }
    token = next;
    openInstances.add(api);
    renderDropdown();
  }

  function onClick() { onInput(); }

  function onKeydown(e) {
    if (!dropdown) return; // closed: host owns the key entirely
    if (e.key === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); e.stopPropagation(); move(-1); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); e.stopPropagation(); commit(activeIndex); }
    else if (e.key === 'Escape') {
      // stopPropagation: a host-level document Escape handler (openTaskDetail()'s
      //   escHandler -> closeTaskDetail()) would otherwise close the whole panel.
      // stopImmediatePropagation: startDescriptionEdit()/the comment-edit textarea
      //   register their OWN Escape->revert listener on this SAME element — plain
      //   stopPropagation does not stop a same-element sibling listener. Callers MUST
      //   attach this typeahead BEFORE their own keydown listener for this to work.
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      close();
    }
  }

  function onBlur() {
    blurTimer = setTimeout(close, 120);
  }
  function onFocus() {
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
  }

  textarea.addEventListener('input', onInput);
  textarea.addEventListener('click', onClick);
  textarea.addEventListener('keydown', onKeydown);
  textarea.addEventListener('blur', onBlur);
  textarea.addEventListener('focus', onFocus);

  function destroy() {
    close();
    textarea.removeEventListener('input', onInput);
    textarea.removeEventListener('click', onClick);
    textarea.removeEventListener('keydown', onKeydown);
    textarea.removeEventListener('blur', onBlur);
    textarea.removeEventListener('focus', onFocus);
    if (scrollEl) scrollEl.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);
  }

  const api = { destroy, close, refresh: onInput };
  return api;
}
